import {
  VISAO_FIELDS, visaoReady,
  type Visao as VisaoContent,
} from '@ads/shared';
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import { SelectField, TextArea } from '../components/Field';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { useConsolidate, useToolReading } from '../lib/queries';
import { useToolDraft } from '../lib/useToolDraft';

/* ─────────────────────────────────────────────────────────────────────
   Visão. À esquerda, os insumos: horizonte e quatro perguntas. À direita,
   a visão finalizada, que a IA escreve a partir deles e do propósito —
   e que a organização pode reescrever; reescrita, a IA não a substitui
   mais (só guarda uma alternativa, para quem quiser voltar).
   ───────────────────────────────────────────────────────────────────── */

const YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 15 }, (_, i) => String(YEAR + 2 + i));

export function Visao() {
  useViewMeta({ nav: 'strategy', crumb: ['Strategy', 'Visão'], j: .36 });
  const nav = useNavigate();
  const { local, setLocal, commit, flush, saveState, status } = useToolDraft('visao');
  const q = useToolReading('visao');
  const consolidate = useConsolidate();
  const [editing, setEditing] = useState(false);
  const [purposeEdit, setPurposeEdit] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);

  const v = local;
  const r = q.data;
  const reading = r?.reading ?? null;
  const updating = !!r && (r.pending || r.stale);
  const edited = !!v?.visionEdited;

  /* a visão da IA chega pela leitura; enquanto ninguém a reescreveu, é ela que aparece */
  useEffect(() => {
    if (!v || v.visionEdited || !reading?.vision || reading.vision === v.vision) return;
    setLocal({ ...v, vision: reading.vision });
  }, [reading?.vision, v, setLocal]);
  useEffect(() => { if (editing) { box.current?.focus(); box.current?.setSelectionRange(box.current.value.length, box.current.value.length); } }, [editing]);

  const patch = (p: Partial<VisaoContent>) => { if (v) commit({ ...v, ...p }); };
  const ready = v ? visaoReady(v) : false;
  const hz = v?.vision ? /^(Até \d{4},)\s*(.*)$/s.exec(v.vision) : null;
  const alt = edited && reading?.vision && reading.vision !== v?.vision ? reading.vision : null;

  return (
    <section className="view on" data-v="visao">
      <button className="back" type="button" onClick={() => { flush(); nav('/strategy'); }}><Ic.ChevronLeft />Voltar para Strategy</button>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Strategy</span><b>Ferramenta 04 / 10</b></div>
          <h1>Visão</h1>
          <p className="lede">Defina onde a organização quer chegar e como deseja ser reconhecida no futuro.</p>
        </div>
        <div className="tools">
          {status && <span className={`g-save ${saveState}`} aria-live="polite">{saveState === 'saved' && <Ic.Check />}{status}</span>}
        </div>
      </div>

      {!v ? <div className="loading-line" /> : (
        <>
          <div className="vi-grid">
            <section className="card vi-form">
              <h3>Estruture a visão de futuro
                <span className="g-help" tabIndex={0} role="img" aria-label="Como funciona"
                  data-tip="Responda com as palavras da organização. Com dois campos preenchidos, a IA junta tudo numa frase — a visão finalizada, ao lado."><Ic.Help /></span>
              </h3>
              <p className="sub">Preencha os campos abaixo para definir a visão de futuro da organização.</p>
              <div className="vi-fields">
                <SelectField id="vi-year" label="Horizonte da visão" value={v.horizonYear ? String(v.horizonYear) : ''}
                  options={v.horizonYear && !YEARS.includes(String(v.horizonYear)) ? [String(v.horizonYear), ...YEARS] : YEARS}
                  placeholder="Escolha o ano" onChange={x => patch({ horizonYear: x ? Number(x) : null })} />
                {VISAO_FIELDS.map(f => (
                  <TextArea key={f.key} id={`vi-${f.key}`} label={f.label} value={v[f.key]} placeholder={f.placeholder} maxLength={400}
                    onChange={x => patch({ [f.key]: x } as Partial<VisaoContent>)} onBlur={flush} />
                ))}
              </div>
            </section>

            <section className={`card vi-final${edited ? ' edited' : ''}`}>
              <div className="gd-h">
                <h3>Visão finalizada</h3>
                {updating && ready && <span className="obj-st busy"><span className="spin" />{edited ? 'Relendo' : 'Escrevendo'}</span>}
              </div>
              <p className="sub">Com base nos insumos preenchidos, esta é a proposta consolidada da visão da organização.</p>

              {editing ? (
                <div className="ta vi-ta">
                  <textarea ref={box} value={v.vision} maxLength={600} rows={5} aria-label="Visão finalizada"
                    onChange={e => patch({ vision: e.target.value, visionEdited: true })} />
                  <div className="bar"><span className="hint">Reescrita, a visão é sua: a IA não a substitui.</span><span className="cnt mono"><b>{v.vision.length}</b>/600</span></div>
                </div>
              ) : v.vision ? (
                <p className={`vi-text${updating && !edited ? ' is-dim' : ''}`}>{hz ? <><b>{hz[1]}</b> {hz[2]}</> : v.vision}</p>
              ) : updating && ready ? (
                <div className="obj-skel vi-skel"><i /><i /><i /></div>
              ) : (
                <p className="gd-empty vi-empty">Preencha pelo menos dois campos. A IA escreve a visão a partir deles e do propósito da organização.</p>
              )}

              {!!reading?.keywords.length && v.vision && (
                <div className={`vi-chips${updating ? ' is-dim' : ''}`}>{reading.keywords.map(k => <span key={k} className="vi-chip">{k}</span>)}</div>
              )}

              {v.vision && (
                <div className="vi-src">
                  <Ic.Sparkle />
                  {edited ? (
                    <div><b>Editada por você</b>
                      <span>{alt ? 'A IA guardou uma versão a partir dos insumos, se quiser comparar.' : 'A IA pontua o alinhamento sobre o seu texto.'}</span></div>
                  ) : (
                    <div><b>Sugestão gerada por IA</b><span>A partir dos insumos preenchidos e do propósito da organização.</span></div>
                  )}
                </div>
              )}
              {alt && !editing && (
                <div className="vi-alt">
                  <p>{alt}</p>
                  <button type="button" className="d-link" onClick={() => patch({ visionEdited: false, vision: alt })}><Ic.Refresh />Usar a versão da IA</button>
                </div>
              )}
              {r?.note && <p className="gd-note"><Ic.Warning />{r.note}</p>}

              {(v.vision || editing) && (
                <div className="vi-actions">
                  {editing
                    ? <button type="button" className="btn-primary sm" onClick={() => { setEditing(false); flush(); }}>Concluir edição<Ic.Check /></button>
                    : <button type="button" className="btn-ghost sm" onClick={() => setEditing(true)}><Ic.Doc />Editar</button>}
                </div>
              )}
            </section>
          </div>

          <section className="card vi-align">
            <div className="vi-align-h">
              <span className="lb">Alinhamento ao propósito</span>
              <span className={`vi-bar${updating ? ' is-dim' : ''}`}><i style={{ '--v': `${reading?.alignment ?? 0}%` } as CSSProperties} /></span>
              <b className="vi-pct">{reading ? `${reading.alignment}%` : '—'}</b>
            </div>
            {reading?.motivo && <p className="vi-why">{reading.motivo}</p>}
            <div className="vi-purpose">
              <span className="lb"><Ic.Target />Propósito</span>
              {purposeEdit ? (
                <div className="ta">
                  <textarea value={v.purpose} maxLength={300} rows={2} autoFocus aria-label="Propósito da organização"
                    onChange={e => patch({ purpose: e.target.value })} onBlur={() => { setPurposeEdit(false); flush(); }} />
                </div>
              ) : (
                <p>{v.purpose ? `“${v.purpose}”` : <em>Sem propósito declarado — o alinhamento fica estimado.</em>}
                  <button type="button" className="d-link" onClick={() => setPurposeEdit(true)}>{v.purpose ? 'Editar' : 'Escrever o propósito'}</button></p>
              )}
            </div>
          </section>
        </>
      )}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => { flush(); nav('/strategy'); }}>Salvar e sair</button>
        <button className="btn-ghost" type="button" onClick={() => { flush(); nav('/strategy/triade'); }}>Voltar</button>
        <button className="btn-primary" type="button" disabled={consolidate.isPending}
          onClick={() => { flush(); consolidate.mutate(undefined, { onSettled: () => nav('/strategy/valores') }); }}>
          {consolidate.isPending ? 'Consolidando o DNA…' : 'Continuar'}<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}
